import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { UNATTRIBUTED_REPO, TICKET_POLL_MS, ticketsApi } from '@/features/ai-dart/api/tickets';
import { DartFormat } from '@/features/ai-dart/format';
import { DEFAULT_TICKET_SORT, TICKET_SORTS, type TicketListResponse, type TicketSort } from '@/features/ai-dart/types';
import { TicketBadge } from '@/features/ai-dart/components/tickets/TicketBadge';
import { FilterChip } from '@/features/ai-dart/components/controls';

const PAGE_SIZE = 10;
/** 상태 칩 표시 순서 — 파이프라인 진행 순서대로 둔다. counts에 없는 상태는 칩을 만들지 않는다. */
const STATUS_ORDER = ['open', 'pending', 'noise', 'eligible', 'autofix_running', 'interrupted', 'needs_human', 'pr_ready', 'failed', 'closed'];
const SEVERITIES = ['S1', 'S2', 'S3', 'S4'];

export function TicketsView(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<TicketListResponse | null>(null);
  const [error, setError] = useState('');
  /** URL의 정렬값은 화이트리스트로 좁힌다 — 서버도 모르는 값을 기본값으로 떨어뜨리지만, 셀렉트가 빈 값을 그리면 안 된다. */
  const sort: TicketSort = TICKET_SORTS.find((option) => option.value === params.get('sort'))?.value ?? DEFAULT_TICKET_SORT;
  const load = useCallback(() => {
    ticketsApi.list({ status: params.get('status') ?? undefined, severity: params.get('severity') ?? undefined, repo: params.get('repo') ?? undefined, q: params.get('q') ?? undefined, hideClosed: params.get('hideClosed') ?? undefined, sort: params.get('sort') ?? undefined })
      .then((value) => { setData(value); setError(''); }).catch((reason: Error) => setError(reason.message));
  }, [params]);
  useEffect(() => { load(); const timer = window.setInterval(load, TICKET_POLL_MS); return () => window.clearInterval(timer); }, [load]);
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1);
  const rows = useMemo(() => data?.tickets.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) ?? [], [data, page]);
  /**
   * 조건 갱신은 patch로 받는다 — 상태 칩과 '종결 제외'처럼 **함께 움직여야 하는 키**가 있다.
   * 한 키씩 두 번 부르면 두 번째 호출이 첫 호출 이전의 params를 복사해 첫 변경을 지운다.
   */
  const setFilters = (patch: Record<string, string>): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value); else next.delete(key);
    }
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };
  /** 같은 값을 다시 누르면 해제한다 — 칩은 셀렉트와 달리 "전체"로 돌아갈 자리가 따로 없다. */
  const toggle = (key: string, value: string): void => setFilters({ [key]: params.get(key) === value ? '' : value });
  /** 켜져 있음은 `hideClosed=1`뿐이다 — 서버도 그 값만 켜짐으로 읽는다. */
  const hideClosed = params.get('hideClosed') === '1';
  /**
   * 상태 줄은 한 벌의 선택이다 — 상태 하나를 고르면 '종결 제외'를 끄고, '종결 제외'를 켜면 상태를 푼다.
   * 둘을 함께 두면 `status=closed`와 서로 반대인 조건이 되어 조건에 맞는 티켓이 0건이 된다.
   */
  const selectStatus = (value: string): void => setFilters({ status: params.get('status') === value ? '' : value, hideClosed: '' });
  /** 검색어의 정본도 URL이다 — 입력칸은 타이핑을 담고, 멈추면 URL(=요청)로 옮긴다. */
  const keywordParam = params.get('q') ?? '';
  const [keyword, setKeyword] = useState(keywordParam);
  // 내가 URL에 쓴 값이 돌아오는 동안에는 입력을 덮어쓰지 않는다. 디바운스가 만료된 직후
  // 새 글자가 입력되면, URL 동기화 effect가 그 글자를 지우는 짧은 경합이 생길 수 있다.
  const submittedKeyword = useRef(keywordParam);
  useEffect(() => {
    if (keywordParam === submittedKeyword.current) return;
    setKeyword(keywordParam);
  }, [keywordParam]);
  // setFilters는 렌더마다 새로 만들어지고 그 렌더의 params를 캡처한다. deps에 넣으면 타이머가 매 렌더
  // 리셋되고, 빼면 타이머가 옛 params를 들고 산다 — 예약 시점이 아니라 **실행 시점의** 함수를 집는다.
  const latestSetFilters = useRef(setFilters);
  useEffect(() => { latestSetFilters.current = setFilters; }, [setFilters]);
  useEffect(() => {
    // URL에 trim한 값을 실으면 그 값이 입력칸으로 되돌아와, 낱말 사이에서 멈춘 사이 끝 공백이 사라진다.
    // 친 그대로 싣고 공백뿐일 때만 q를 지운다 — 서버도 공백뿐인 검색어는 조건으로 걸지 않는다.
    const next = keyword.trim() ? keyword : '';
    if (next === keywordParam) return;
    const timer = window.setTimeout(() => {
      submittedKeyword.current = next;
      latestSetFilters.current({ q: next });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [keyword, keywordParam]);
  const counts = data?.counts ?? {};
  /** '종결 제외'로 보게 되는 건수 — 상태 칩 건수와 같은 출처(서버 집계)에서 뺀다. */
  const unclosedCount = (data?.total ?? 0) - (counts.closed ?? 0);
  const pageCount = Math.max(1, Math.ceil((data?.tickets.length ?? 0) / PAGE_SIZE));
  const detailQuery = params.toString();
  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between"><div><h1 className="text-xl font-bold">AI-DART 티켓</h1><p className="text-[12px] text-muted">15초마다 갱신 · 필터는 URL에 보존됩니다.</p></div><span className="text-[12px] text-muted">전체 {data?.total ?? 0}건</span></header>
      <section className="space-y-2 rounded-lg border border-line bg-surface p-3">
        {/* 검색 — 서버가 제목·fingerprint·repo·증거 발췌를 훑는다(증거는 목록에 실리지 않아 화면에선 못 거른다). */}
        <input
          type="search"
          aria-label="티켓 검색"
          placeholder="제목 · fingerprint · repo · 로그 발췌 검색"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          className="w-full max-w-[360px] rounded-md border border-line bg-surface px-2.5 py-1.5 text-[12px] outline-none focus:border-accent"
        />
        {/* 상태 — 건수를 칩에 함께 싣는다. 셀렉트를 열지 않아도 분포가 한눈에 보여야 한다. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <FilterChip active={!params.get('status') && !hideClosed} onClick={() => setFilters({ status: '', hideClosed: '' })}>
            전체 {data?.total ?? 0}
          </FilterChip>
          {/* 종결 제외 — 상태를 고르는 칩이 아니라 끝난 것만 걷어 내는 스위치다. 가장 자주 쓰는 조건이라 상태 칩 앞에 둔다. */}
          <FilterChip active={hideClosed} onClick={() => setFilters({ hideClosed: hideClosed ? '' : '1', status: '' })}>
            종결 제외 {unclosedCount}
          </FilterChip>
          {STATUS_ORDER.filter((value) => counts[value]).map((value) => (
            <FilterChip key={value} active={params.get('status') === value} onClick={() => selectStatus(value)}>
              {value} {counts[value]}
            </FilterChip>
          ))}
        </div>
        {/* 심각도 · 레포 */}
        <div className="flex flex-wrap items-center gap-1.5">
          {SEVERITIES.map((value) => (
            <FilterChip key={value} active={params.get('severity') === value} onClick={() => toggle('severity', value)}>
              {value}
            </FilterChip>
          ))}
          {(data?.repos.length ?? 0) > 0 && <span className="px-1 text-faint">·</span>}
          {(data?.repos ?? []).map((value) => (
            <FilterChip
              key={value}
              active={params.get('repo') === value}
              title={value === UNATTRIBUTED_REPO ? '귀속 미상 — 레포가 정해지지 않아 자동수정 대상이 아닙니다' : value}
              onClick={() => toggle('repo', value)}
            >
              {value === UNATTRIBUTED_REPO ? '귀속 미상' : value}
            </FilterChip>
          ))}
        </div>
      </section>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[12px] text-muted">{data?.tickets.length ?? 0}건 표시 · 시각 KST</p>
        <label className="flex items-center gap-1.5 text-[12px] text-muted">
          정렬
          <select
            aria-label="정렬"
            value={sort}
            onChange={(event) => setFilters({ sort: event.target.value === DEFAULT_TICKET_SORT ? '' : event.target.value })}
            className="rounded-md border border-line bg-surface px-2 py-1 text-[12px]"
          >
            {TICKET_SORTS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
      </div>
      {error && <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-[12px] text-rose-700">{error}</div>}
      <div className="overflow-x-auto rounded-lg border border-line bg-surface">
        <table className="w-full text-left text-[12px]"><thead className="border-b border-line bg-line-soft text-muted"><tr><th className="p-3">ID</th><th>상태</th><th>심각도</th><th>제목</th><th>레포</th><th>발생</th><th className="pr-3">최근</th></tr></thead>
          <tbody>{rows.map((ticket) => <tr key={ticket.id} className="border-b border-line last:border-0"><td className="p-3 font-mono"><Link className="text-accent hover:underline" to={`/ai-dart/tickets/${ticket.id}${detailQuery ? `?${detailQuery}` : ''}`}>#{ticket.id}</Link></td><td><TicketBadge value={ticket.status} />{ticket.resolution && <span className="ml-1 text-[10px] text-faint">{ticket.resolution}</span>}{ticket.recurrenceOf && <span className="ml-1 text-[10px] text-faint">↻#{ticket.recurrenceOf}</span>}</td><td><TicketBadge value={ticket.severity} /></td><td className="max-w-[420px] py-3"><div className="font-medium">{ticket.title ?? '(제목 없음)'}</div><div className="truncate text-[10px] text-faint">{ticket.fingerprint}</div></td><td>{ticket.repo ?? '귀속 미상'}</td><td>{ticket.occurrenceCount}</td><td className="pr-3 whitespace-nowrap">{DartFormat.kst(ticket.lastSeenAt)}</td></tr>)}</tbody>
        </table>
        {!error && rows.length === 0 && <div className="p-8 text-center text-muted">조건에 맞는 티켓이 없습니다.</div>}
      </div>
      <div className="flex items-center justify-end gap-2 text-[12px]"><button disabled={page <= 1} onClick={() => setFilters({ page: String(page - 1) })}>이전</button><span>{page} / {pageCount}</span><button disabled={page >= pageCount} onClick={() => setFilters({ page: String(page + 1) })}>다음</button></div>
    </div>
  );
}
